import { useEffect, useMemo, useState } from "react";
import { clearBrand } from "@/components/customer/Brand";
import CustomerHeader, { CustomerFooter } from "@/components/customer/CustomerHeader";
import SearchBar from "@/components/customer/SearchBar";
import HotelCard from "@/components/customer/HotelCard";
import SearchFilters from "@/components/customer/SearchFilters";
import { Skeleton } from "@/components/ui/skeleton";
import { http, useStay, errMsg, stayQuery, ctxParams } from "@/lib/api";
import { useShopper } from "@/components/customer/ShopperProvider";
import { SlidersHorizontal } from "lucide-react";
import { useT, useLoc } from "@/lib/i18n";

const SORTS = [
  { id: "rec", label: "Rekomendasi" },
  { id: "price", label: "Harga terendah" },
  { id: "rating", label: "Rating tertinggi" },
];

export default function SearchResults() {
  const stay = useStay();
  const key = stayQuery(stay);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sort, setSort] = useState("rec");
  const { member, country } = useShopper();
  const t = useT();
  const loc = useLoc();
  useEffect(() => { clearBrand(); }, []);
  const [showFilters, setShowFilters] = useState(false);
  const [f, setF] = useState({ price: 0, stars: [], fac: [], freeCancel: false, breakfast: false, payAtHotel: false });

  useEffect(() => {
    setData(null); setError("");
    http.get(`/public/search?${key}`, { params: ctxParams(country) }).then((r) => {
      setData(r.data.results);
      const max = Math.max(1000000, ...r.data.results.map((x) => x.offer?.avg_per_night || 0));
      setF((o) => ({ ...o, price: Math.ceil(max / 100000) * 100000 }));
    }).catch((e) => setError(errMsg(e)));
  }, [key, country, member?.id]);

  const maxPrice = useMemo(() => Math.ceil(Math.max(1000000, ...(data || []).map((x) => x.offer?.avg_per_night || 0)) / 100000) * 100000, [data]);
  const facilities = [...new Set((data || []).flatMap((x) => loc(x.property, "facilities") || []))];

  const list = useMemo(() => {
    let l = (data || []).filter(({ property: p, offer, badges }) =>
      (!offer || offer.avg_per_night <= f.price) && (!f.stars.length || f.stars.includes(p.stars)) &&
      (!f.freeCancel || badges.free_cancellation) && (!f.breakfast || badges.breakfast) && (!f.payAtHotel || badges.pay_at_hotel) &&
      f.fac.every((x) => (loc(p, "facilities") || []).includes(x)));
    if (sort === "price") l = [...l].sort((a, b) => (a.offer?.avg_per_night ?? 1e12) - (b.offer?.avg_per_night ?? 1e12));
    if (sort === "rating") l = [...l].sort((a, b) => b.property.rating - a.property.rating);
    return l;
  }, [data, f, sort, loc]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <CustomerHeader />
      <div className="border-b border-slate-200 bg-[#EAF2FF]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6"><SearchBar initial={stay} compact /></div>
      </div>
      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-8 sm:px-6 lg:grid-cols-4">
        <div className="lg:hidden">
          <button data-testid="toggle-filters-button" onClick={() => setShowFilters(!showFilters)}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-700">
            <SlidersHorizontal className="h-4 w-4" /> {t(showFilters ? "Sembunyikan filter" : "Filter")}
          </button>
        </div>
        <div className={showFilters ? "" : "hidden lg:block"}>
          <SearchFilters f={f} setF={setF} maxPrice={maxPrice} facilities={facilities} />
        </div>
        <section className="flex flex-col gap-4 lg:col-span-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 data-testid="search-result-count" className="font-heading text-xl font-bold text-slate-900">
              {data ? (stay.city ? t("{n} properti di {c}", { n: list.length, c: stay.city }) : t("{n} properti ditemukan", { n: list.length })) : t("Mencari...")}
            </h1>
            <div className="flex rounded-xl border border-slate-200 bg-white p-1">
              {SORTS.map((s) => (
                <button key={s.id} data-testid={`sort-${s.id}`} onClick={() => setSort(s.id)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${sort === s.id ? "bg-[#5392F9] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  {t(s.label)}
                </button>
              ))}
            </div>
          </div>
          {error && <p data-testid="search-error" className="rounded-xl bg-red-50 p-4 text-red-700">{error}</p>}
          {!data && !error && [0, 1, 2].map((i) => <Skeleton key={i} className="h-52 rounded-2xl" />)}
          {data && list.length === 0 && (
            <p data-testid="search-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
              {t("Tidak ada properti yang cocok. Coba ubah filter atau tujuan.")}
            </p>
          )}
          {list.map((item, i) => <HotelCard key={item.property.id} item={item} stay={stay} index={i} />)}
        </section>
      </main>
      <CustomerFooter />
    </div>
  );
}
